(() => {
  const geography = document.querySelector('[data-geography]');

  if (!geography) {
    return;
  }

  const dropdown = geography.querySelector('.region-dropdown');
  const toggleButton = dropdown?.querySelector('.region-dropdown__toggle');
  const toggleLabel = toggleButton?.querySelector('span');
  const regionList = dropdown?.querySelector('.region-dropdown__list');
  const regionItems = Array.from(dropdown?.querySelectorAll('.region-item') || []);
  const viewport = geography.querySelector('[data-map-viewport]');
  const scene = geography.querySelector('[data-map-scene]');
  const mapModeButton = geography.querySelector('[data-map-mode-toggle]');
  const mapModeLabel = geography.querySelector('[data-map-mode-label]');
  const mapHint = geography.querySelector('[data-map-hint]');
  const mapTooltip = geography.querySelector('[data-map-tooltip]');
  const mapTooltipTitle = mapTooltip?.querySelector('[data-map-tooltip-title]');
  const mapTooltipSummary = mapTooltip?.querySelector('[data-map-tooltip-summary]');
  const zoomInButton = geography.querySelector('[data-map-zoom-in]');
  const zoomOutButton = geography.querySelector('[data-map-zoom-out]');
  const resetButton = geography.querySelector('[data-map-reset]');
  const panel = geography.querySelector('[data-school-panel]');
  const panelTitle = panel?.querySelector('.school-popup__title');
  const panelSummary = panel?.querySelector('[data-school-summary]');
  const panelList = panel?.querySelector('.school-popup__list');
  const panelScrollbar = panel?.querySelector('[data-school-scrollbar]');
  const panelScrollbarThumb = panel?.querySelector('[data-school-scrollbar-thumb]');
  const panelCloseButton = panel?.querySelector('.school-popup__close');
  const mapRegionElements = Array.from(geography.querySelectorAll('.schools__geography-map-region'));

  if (
    !dropdown ||
    !toggleButton ||
    !toggleLabel ||
    !regionList ||
    !viewport ||
    !scene ||
    !mapModeButton ||
    !mapModeLabel ||
    !mapHint ||
    !mapTooltip ||
    !mapTooltipTitle ||
    !mapTooltipSummary ||
    !zoomInButton ||
    !zoomOutButton ||
    !resetButton ||
    !panel ||
    !panelTitle ||
    !panelSummary ||
    !panelList ||
    !panelScrollbar ||
    !panelScrollbarThumb ||
    !panelCloseButton
  ) {
    return;
  }

  const mobileMedia = window.matchMedia('(max-width: 640px)');
  const desktopMedia = window.matchMedia('(min-width: 901px)');
  const coarsePointerMedia = window.matchMedia('(hover: none) and (pointer: coarse)');
  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  const regionData = new Map();
  const mapRegions = new Map(mapRegionElements.map((region) => [region.dataset.id, region]));
  const pointers = new Map();
  const transform = {
    x: 0,
    y: 0,
    scale: 1,
  };
  let selectedRegionId = null;
  let mapModeActive = false;
  let renderFrame = null;
  let resizeFrame = null;
  let animationTimer = null;
  let pointerMoved = false;
  let suppressMapClick = false;
  let lastSelectionControl = null;
  let tooltipFrame = null;
  let dropdownSelectionScrollFrame = null;
  let dropdownSelectionScrollTimer = null;
  let dropdownSelectionTransitionHandler = null;
  let mapModeIdleTimer = null;
  let mapModeButtonPositionFrame = null;
  let panelScrollbarFrame = null;

  regionItems.forEach((item) => {
    let schools = [];

    try {
      schools = JSON.parse(item.dataset.schools || '[]');
    } catch (error) {
      schools = [];
    }

    regionData.set(item.dataset.id, {
      count: Number(item.dataset.count) || schools.length,
      item,
      name: item.dataset.name?.trim() || '',
      schools,
    });
  });

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const mapAspectWidth = 1414;
  const mapAspectHeight = 758;
  const desktopMinimumSceneHeight = 540;
  const mapModeIdleDelay = 7000;
  const selectedRegionFocusMultiplier = 1.2;

  const getSceneBaseSize = () => {
    const viewportWidth = viewport.clientWidth;
    const viewportHeight = viewport.clientHeight;
    const heightConstrainedWidth = viewportHeight * mapAspectWidth / mapAspectHeight;
    const minimumDesktopWidth = desktopMinimumSceneHeight * mapAspectWidth / mapAspectHeight;
    const width = mobileMedia.matches
      ? viewportWidth
      : Math.min(
        viewportWidth,
        Math.max(
          heightConstrainedWidth,
          desktopMedia.matches ? minimumDesktopWidth : 0
        )
      );

    return {
      width,
      height: width * mapAspectHeight / mapAspectWidth,
    };
  };

  const getRegionBaseBounds = (region) => {
    const sceneSize = getSceneBaseSize();
    const left = Number.parseFloat(region.style.left) || 0;
    const top = Number.parseFloat(region.style.top) || 0;
    const width = Number.parseFloat(region.style.width) || 0;
    const height = Number.parseFloat(region.style.height) || 0;

    return {
      height: sceneSize.height * height / 100,
      left: sceneSize.width * left / 100,
      top: sceneSize.height * top / 100,
      width: sceneSize.width * width / 100,
    };
  };

  const shouldReduceMotion = () => (
    reducedMotionMedia.matches ||
    document.body.dataset.accessibility === 'true' ||
    document.body.dataset.noImages === 'true'
  );

  const getMaximumScale = () => mobileMedia.matches ? 6.25 : 5.25;

  const getTransformLimits = (scale) => {
    const viewportWidth = viewport.clientWidth;
    const viewportHeight = viewport.clientHeight;
    const sceneSize = getSceneBaseSize();
    const contentWidth = sceneSize.width * scale;
    const contentHeight = sceneSize.height * scale;
    let coverageWidth = viewportWidth;
    let coverageHeight = viewportHeight;

    if (panel.classList.contains('active')) {
      if (!mobileMedia.matches) {
        coverageWidth -= Math.min(panel.offsetWidth + 24, viewportWidth * 0.42);
      }
    }

    const focusMarginX = (
      mobileMedia.matches && selectedRegionId && scale > 1.001
        ? viewportWidth * 0.38
        : 0
    );
    const focusMarginY = (
      mobileMedia.matches && selectedRegionId && scale > 1.001
        ? viewportHeight * 0.28
        : 0
    );

    const horizontal = contentWidth <= coverageWidth
      ? [(coverageWidth - contentWidth) / 2, (coverageWidth - contentWidth) / 2]
      : [coverageWidth - contentWidth - focusMarginX, focusMarginX];
    const vertical = contentHeight <= coverageHeight
      ? [(coverageHeight - contentHeight) / 2, (coverageHeight - contentHeight) / 2]
      : [coverageHeight - contentHeight - focusMarginY, focusMarginY];

    return {
      maxX: horizontal[1],
      maxY: vertical[1],
      minX: horizontal[0],
      minY: vertical[0],
    };
  };

  const constrainTransform = ({ x, y, scale }) => {
    const constrainedScale = clamp(scale, 1, getMaximumScale());
    const limits = getTransformLimits(constrainedScale);

    return {
      scale: constrainedScale,
      x: clamp(x, limits.minX, limits.maxX),
      y: clamp(y, limits.minY, limits.maxY),
    };
  };

  const getBaseTransform = () => {
    const sceneSize = getSceneBaseSize();

    return {
      scale: 1,
      x: 0,
      y: Math.min(0, (viewport.clientHeight - sceneSize.height) / 2),
    };
  };

  const updateControlState = () => {
    const atMinimumScale = transform.scale <= 1.001;
    const atMaximumScale = transform.scale >= getMaximumScale() - 0.001;
    const baseTransform = constrainTransform(getBaseTransform());
    const atBasePosition = (
      atMinimumScale &&
      Math.abs(transform.x - baseTransform.x) < 0.5 &&
      Math.abs(transform.y - baseTransform.y) < 0.5
    );

    zoomOutButton.disabled = atMinimumScale;
    zoomInButton.disabled = atMaximumScale;
    resetButton.disabled = atBasePosition;
  };

  const renderTransform = () => {
    const sceneSize = getSceneBaseSize();

    renderFrame = null;
    scene.style.setProperty('--map-base-width', `${sceneSize.width}px`);
    scene.style.setProperty('--map-x', `${transform.x}px`);
    scene.style.setProperty('--map-y', `${transform.y}px`);
    scene.style.setProperty('--map-scale', transform.scale.toFixed(4));
    scene.style.setProperty(
      '--map-render-width',
      `${sceneSize.width * transform.scale}px`
    );
    scene.style.setProperty(
      '--map-marker-compensation',
      mobileMedia.matches ? '1' : (1 / transform.scale).toFixed(4)
    );
    mapRegionElements.forEach((region) => {
      const marker = region.querySelector('[data-map-marker]');

      if (!marker) {
        return;
      }

      const regionBounds = getRegionBaseBounds(region);
      const visibleRegionSide = Math.min(regionBounds.width, regionBounds.height) * transform.scale;
      const markerSize = mobileMedia.matches
        ? clamp(visibleRegionSide * 0.16, 1.25, 2.5)
        : clamp(visibleRegionSide * 0.18, 2, 4);

      marker.style.setProperty('--map-marker-size', `${markerSize.toFixed(2)}px`);
    });
    updateControlState();
  };

  const scheduleTransformRender = () => {
    if (renderFrame === null) {
      renderFrame = requestAnimationFrame(renderTransform);
    }
  };

  const readRenderedTransform = () => {
    const sceneStyles = getComputedStyle(scene);
    const renderedTransform = sceneStyles.transform;

    if (mobileMedia.matches) {
      const sceneSize = getSceneBaseSize();
      const renderedWidth = scene.getBoundingClientRect().width;

      return {
        scale: renderedWidth / Math.max(sceneSize.width, 1),
        x: Number.parseFloat(sceneStyles.left) || 0,
        y: Number.parseFloat(sceneStyles.top) || 0,
      };
    }

    if (!renderedTransform || renderedTransform === 'none') {
      return null;
    }

    if (typeof DOMMatrixReadOnly === 'function') {
      try {
        const matrix = new DOMMatrixReadOnly(renderedTransform);

        return {
          scale: matrix.a,
          x: matrix.e,
          y: matrix.f,
        };
      } catch (error) {
        // Старые WebKit-сборки читают matrix(), но не создают DOMMatrixReadOnly.
      }
    }

    const matrixValues = renderedTransform.match(/^matrix\(([^)]+)\)$/)?.[1]
      .split(',')
      .map(Number);

    if (!matrixValues || matrixValues.length !== 6) {
      return null;
    }

    return {
      scale: matrixValues[0],
      x: matrixValues[4],
      y: matrixValues[5],
    };
  };

  const stopTransformAnimation = () => {
    if (!scene.classList.contains('is-animating')) {
      return;
    }

    const rendered = readRenderedTransform();

    window.clearTimeout(animationTimer);
    animationTimer = null;
    scene.classList.remove('is-animating');

    if (rendered) {
      Object.assign(transform, constrainTransform(rendered));
      renderTransform();
    }
  };

  const setTransform = (nextTransform, { animate = false } = {}) => {
    const constrained = constrainTransform(nextTransform);
    const allowAnimation = animate && !shouldReduceMotion();

    hideRegionTooltip();
    window.clearTimeout(animationTimer);
    animationTimer = null;

    if (renderFrame !== null) {
      cancelAnimationFrame(renderFrame);
      renderFrame = null;
      renderTransform();
    }

    scene.classList.toggle('is-animating', allowAnimation);

    if (allowAnimation) {
      void scene.offsetWidth;
      animationTimer = window.setTimeout(() => {
        scene.classList.remove('is-animating');
        animationTimer = null;
      }, 620);
    }

    Object.assign(transform, constrained);
    renderTransform();
  };

  const resetMap = ({ animate = true } = {}) => {
    stopTransformAnimation();
    setTransform(getBaseTransform(), { animate });
  };

  const zoomAtPoint = (factor, point, { animate = false } = {}) => {
    stopTransformAnimation();

    const nextScale = clamp(transform.scale * factor, 1, getMaximumScale());
    const sceneX = (point.x - transform.x) / transform.scale;
    const sceneY = (point.y - transform.y) / transform.scale;

    setTransform({
      scale: nextScale,
      x: point.x - sceneX * nextScale,
      y: point.y - sceneY * nextScale,
    }, { animate });
  };

  const zoomFromCenter = (factor) => {
    zoomAtPoint(factor, {
      x: viewport.clientWidth / 2,
      y: viewport.clientHeight / 2,
    }, { animate: true });
  };

  const getSchoolWord = (count) => {
    const mod10 = count % 10;
    const mod100 = count % 100;

    if (mod10 === 1 && mod100 !== 11) {
      return 'школа';
    }

    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
      return 'школы';
    }

    return 'школ';
  };

  const updatePanelScrollbar = () => {
    panelScrollbarFrame = null;

    const listHeight = panelList.clientHeight;
    const scrollHeight = panelList.scrollHeight;
    const maximumScroll = Math.max(scrollHeight - listHeight, 0);
    const visible = (
      panel.classList.contains('active') &&
      listHeight > 0 &&
      maximumScroll > 1
    );

    panelScrollbar.classList.toggle('is-visible', visible);

    if (!visible) {
      panelScrollbarThumb.style.height = '';
      panelScrollbarThumb.style.transform = '';
      return;
    }

    const thumbHeight = clamp(
      listHeight * listHeight / Math.max(scrollHeight, 1),
      28,
      listHeight
    );
    const maximumThumbOffset = Math.max(listHeight - thumbHeight, 0);
    const thumbOffset = maximumScroll > 0
      ? panelList.scrollTop / maximumScroll * maximumThumbOffset
      : 0;

    panelScrollbar.style.top = `${panelList.offsetTop}px`;
    panelScrollbar.style.height = `${listHeight}px`;
    panelScrollbarThumb.style.height = `${thumbHeight}px`;
    panelScrollbarThumb.style.transform = `translateY(${thumbOffset}px)`;
  };

  const schedulePanelScrollbarUpdate = () => {
    if (panelScrollbarFrame === null) {
      panelScrollbarFrame = requestAnimationFrame(updatePanelScrollbar);
    }
  };

  const requiresExplicitMapMode = () => mobileMedia.matches || coarsePointerMedia.matches;

  const updateMapModeButtonPosition = () => {
    mapModeButtonPositionFrame = null;

    if (!mobileMedia.matches || !mapModeActive) {
      geography.classList.remove('has-floating-map-mode-toggle');
      return;
    }

    const headerBottom = Math.max(
      0,
      document.querySelector('.header')?.getBoundingClientRect().bottom || 0
    );
    const viewportRect = viewport.getBoundingClientRect();
    const geographyRect = geography.getBoundingClientRect();
    const geographyIsVisible = (
      geographyRect.bottom > headerBottom &&
      geographyRect.top < document.documentElement.clientHeight
    );
    const topControlIsHidden = viewportRect.top < headerBottom + 12;

    geography.classList.toggle(
      'has-floating-map-mode-toggle',
      geographyIsVisible && topControlIsHidden
    );
  };

  const scheduleMapModeButtonPositionUpdate = () => {
    if (mapModeButtonPositionFrame === null) {
      mapModeButtonPositionFrame = requestAnimationFrame(updateMapModeButtonPosition);
    }
  };

  const updateMapModePresentation = () => {
    const compact = mobileMedia.matches;

    mapModeLabel.textContent = mapModeActive
      ? 'Завершить управление'
      : 'Управлять';
    if (compact || coarsePointerMedia.matches) {
      mapHint.textContent = mapModeActive
        ? 'Перетаскивайте карту одним пальцем, изменяйте масштаб двумя'
        : 'Включите режим управления, чтобы перемещать и масштабировать карту';
    } else {
      mapHint.textContent = 'Перетаскивайте карту мышью; Ctrl/⌘ + колесо или жест — масштаб';
    }
  };

  const hideRegionTooltip = () => {
    if (tooltipFrame !== null) {
      cancelAnimationFrame(tooltipFrame);
      tooltipFrame = null;
    }

    mapTooltip.classList.remove('is-visible');
    mapTooltip.setAttribute('aria-hidden', 'true');
  };

  const positionRegionTooltip = (clientX, clientY) => {
    if (tooltipFrame !== null) {
      cancelAnimationFrame(tooltipFrame);
    }

    tooltipFrame = requestAnimationFrame(() => {
      tooltipFrame = null;

      if (!mapTooltip.classList.contains('is-visible')) {
        return;
      }

      const viewportRect = viewport.getBoundingClientRect();
      const tooltipRect = mapTooltip.getBoundingClientRect();
      const padding = 10;
      let left = clientX - viewportRect.left + 14;
      let top = clientY - viewportRect.top - tooltipRect.height - 12;

      if (top < padding) {
        top = clientY - viewportRect.top + 16;
      }

      left = clamp(left, padding, viewportRect.width - tooltipRect.width - padding);
      top = clamp(top, padding, viewportRect.height - tooltipRect.height - padding);
      mapTooltip.style.left = `${left}px`;
      mapTooltip.style.top = `${top}px`;
    });
  };

  const showRegionTooltip = (region, point = null) => {
    const data = regionData.get(region.dataset.id);

    if (
      mobileMedia.matches ||
      !data ||
      scene.classList.contains('is-animating') ||
      (coarsePointerMedia.matches && !point)
    ) {
      return;
    }

    mapTooltipTitle.textContent = data.name;
    mapTooltipSummary.textContent = `${data.count} ${getSchoolWord(data.count)} в проекте`;
    mapTooltip.classList.add('is-visible');
    mapTooltip.setAttribute('aria-hidden', 'false');

    if (point) {
      positionRegionTooltip(point.clientX, point.clientY);
      return;
    }

    const regionRect = region.getBoundingClientRect();
    positionRegionTooltip(
      regionRect.left + regionRect.width / 2,
      regionRect.top + regionRect.height / 2
    );
  };

  const markerClearanceDirections = Array.from({ length: 16 }, (_, index) => {
    const angle = index * Math.PI * 2 / 16;

    return {
      x: Math.cos(angle),
      y: Math.sin(angle),
    };
  });

  const isPointInsidePath = (path, point, x, y) => {
    point.x = x;
    point.y = y;

    try {
      return path.isPointInFill(point);
    } catch (error) {
      return false;
    }
  };

  const getPointClearance = (path, point, x, y, maximumRadius) => {
    if (!isPointInsidePath(path, point, x, y)) {
      return -1;
    }

    let minimumRadius = 0;
    let radiusLimit = maximumRadius;

    for (let iteration = 0; iteration < 7; iteration += 1) {
      const radius = (minimumRadius + radiusLimit) / 2;
      const circleFits = markerClearanceDirections.every((direction) => (
        isPointInsidePath(
          path,
          point,
          x + direction.x * radius,
          y + direction.y * radius
        )
      ));

      if (circleFits) {
        minimumRadius = radius;
      } else {
        radiusLimit = radius;
      }
    }

    return minimumRadius;
  };

  const findRegionMarkerPoint = (svg, paths) => {
    const viewBox = svg.viewBox?.baseVal;
    const point = typeof svg.createSVGPoint === 'function'
      ? svg.createSVGPoint()
      : null;

    if (
      !viewBox ||
      !point ||
      !paths.some((path) => typeof path.isPointInFill === 'function')
    ) {
      return null;
    }

    const pathBounds = paths
      .filter((path) => typeof path.isPointInFill === 'function')
      .map((path) => {
        try {
          const bounds = path.getBBox();

          return {
            bounds,
            maximumClearance: Math.min(bounds.width, bounds.height) / 2,
            path,
          };
        } catch (error) {
          return null;
        }
      })
      .filter(Boolean)
      .sort((first, second) => second.maximumClearance - first.maximumClearance);
    const gridSize = 22;
    let bestCandidate = null;
    let bestClearance = -1;
    let bestCenterDistance = Infinity;

    const considerCandidate = (path, bounds, x, y) => {
      const maximumRadius = Math.max(0, Math.min(
        x - bounds.x,
        bounds.x + bounds.width - x,
        y - bounds.y,
        bounds.y + bounds.height - y
      ));

      if (maximumRadius <= bestClearance) {
        return null;
      }

      const clearance = getPointClearance(path, point, x, y, maximumRadius);

      if (clearance < 0) {
        return null;
      }

      const centerDistance = Math.hypot(
        (x - (viewBox.x + viewBox.width / 2)) / Math.max(viewBox.width, 1),
        (y - (viewBox.y + viewBox.height / 2)) / Math.max(viewBox.height, 1)
      );

      if (
        clearance > bestClearance + 0.01 ||
        (Math.abs(clearance - bestClearance) <= 0.01 && centerDistance < bestCenterDistance)
      ) {
        bestCandidate = { x, y };
        bestClearance = clearance;
        bestCenterDistance = centerDistance;
      }

      return { clearance, x, y };
    };

    pathBounds.forEach(({ bounds, maximumClearance, path }) => {
      if (maximumClearance <= bestClearance) {
        return;
      }

      const stepX = bounds.width / gridSize;
      const stepY = bounds.height / gridSize;
      let pathCandidate = null;

      for (let row = 0; row < gridSize; row += 1) {
        for (let column = 0; column < gridSize; column += 1) {
          const candidate = considerCandidate(
            path,
            bounds,
            bounds.x + (column + 0.5) * stepX,
            bounds.y + (row + 0.5) * stepY
          );

          if (!pathCandidate || (candidate && candidate.clearance > pathCandidate.clearance)) {
            pathCandidate = candidate || pathCandidate;
          }
        }
      }

      if (!pathCandidate) {
        return;
      }

      let refinementStepX = stepX / 2;
      let refinementStepY = stepY / 2;

      for (let iteration = 0; iteration < 3; iteration += 1) {
        const centerX = pathCandidate.x;
        const centerY = pathCandidate.y;

        for (let row = -2; row <= 2; row += 1) {
          for (let column = -2; column <= 2; column += 1) {
            const candidate = considerCandidate(
              path,
              bounds,
              centerX + column * refinementStepX,
              centerY + row * refinementStepY
            );

            if (candidate && candidate.clearance > pathCandidate.clearance) {
              pathCandidate = candidate;
            }
          }
        }

        refinementStepX /= 2;
        refinementStepY /= 2;
      }
    });

    if (!bestCandidate) {
      return null;
    }

    return {
      xRatio: (bestCandidate.x - viewBox.x) / Math.max(viewBox.width, 1),
      yRatio: (bestCandidate.y - viewBox.y) / Math.max(viewBox.height, 1),
    };
  };

  mapRegionElements.forEach((region) => {
    const data = regionData.get(region.dataset.id);

    if (!data) {
      return;
    }

    region.dataset.count = String(data.count);
    region.setAttribute(
      'aria-label',
      `${data.name}: ${data.count} ${getSchoolWord(data.count)}. Показать регион`
    );

    const marker = region.querySelector('[data-map-marker]');
    const svg = region.querySelector('svg');
    const paths = Array.from(svg?.querySelectorAll('path') || []);

    if (!marker || !svg || paths.length === 0) {
      return;
    }

    const markerPoint = findRegionMarkerPoint(svg, paths);

    marker.style.setProperty(
      '--map-marker-x',
      `${(markerPoint?.xRatio || 0.5) * 100}%`
    );
    marker.style.setProperty(
      '--map-marker-y',
      `${(markerPoint?.yRatio || 0.5) * 100}%`
    );
  });

  const setDropdownOpen = (open, { focusSelected = false } = {}) => {
    dropdown.classList.toggle('open', open);
    toggleButton.setAttribute('aria-expanded', String(open));
    regionList.setAttribute('aria-hidden', String(!open));
    regionList.inert = !open;
    regionItems.forEach((item) => {
      item.tabIndex = open ? 0 : -1;
    });

    if (open && focusSelected) {
      const selectedItem = selectedRegionId ? regionData.get(selectedRegionId)?.item : null;
      (selectedItem || regionItems[0])?.focus();
    }
  };

  const getFocusSafeArea = () => {
    const viewportWidth = viewport.clientWidth;
    const viewportHeight = viewport.clientHeight;
    const mobile = mobileMedia.matches;
    const padding = mobile ? 18 : 28;
    const safeArea = {
      bottom: viewportHeight - padding,
      left: padding,
      right: viewportWidth - padding,
      top: mobile ? 88 : padding,
    };

    if (panel.classList.contains('active')) {
      if (!mobile) {
        safeArea.right -= Math.min(panel.offsetWidth + 24, viewportWidth * 0.42);
      }
    }

    if (safeArea.right - safeArea.left < 120) {
      safeArea.right = viewportWidth - padding;
    }

    if (safeArea.bottom - safeArea.top < 120) {
      safeArea.top = padding;
      safeArea.bottom = viewportHeight - padding;
    }

    return safeArea;
  };

  const focusRegion = (regionId, { animate = true } = {}) => {
    const mapRegion = mapRegions.get(regionId);

    if (!mapRegion) {
      return;
    }

    stopTransformAnimation();

    const safeArea = getFocusSafeArea();
    const safeWidth = safeArea.right - safeArea.left;
    const safeHeight = safeArea.bottom - safeArea.top;
    const regionBounds = getRegionBaseBounds(mapRegion);
    const regionCenterX = regionBounds.left + regionBounds.width / 2;
    const regionCenterY = regionBounds.top + regionBounds.height / 2;
    const longestRegionSide = Math.max(regionBounds.width, regionBounds.height, 1);
    const mobileTargetSize = clamp(viewport.clientWidth * 0.14, 42, 52);
    const adaptiveMobileScale = clamp(
      mobileTargetSize / longestRegionSide,
      1.45,
      getMaximumScale()
    );
    const desktopTargetSize = clamp(
      Math.min(safeWidth, safeHeight) * 0.24,
      88,
      112
    );
    const adaptiveDesktopScale = clamp(
      desktopTargetSize / longestRegionSide,
      1.15,
      2.4
    );
    const maximumFitScale = Math.min(
      safeWidth / Math.max(regionBounds.width * 1.5, 1),
      safeHeight / Math.max(regionBounds.height * 1.5, 1)
    );
    const baseTargetScale = mobileMedia.matches
      ? clamp(Math.min(adaptiveMobileScale, maximumFitScale), 1.35, getMaximumScale())
      : clamp(Math.min(adaptiveDesktopScale, maximumFitScale), 1.1, 2.4);
    const targetScale = clamp(
      baseTargetScale * selectedRegionFocusMultiplier,
      mobileMedia.matches ? 1.35 : 1.1,
      getMaximumScale()
    );
    const targetCenterX = safeArea.left + safeWidth / 2;
    const targetCenterY = safeArea.top + safeHeight / 2;

    setTransform({
      scale: targetScale,
      x: targetCenterX - regionCenterX * targetScale,
      y: targetCenterY - regionCenterY * targetScale,
    }, { animate });
  };

  const clearDropdownSelectionScrollWait = () => {
    if (dropdownSelectionScrollTimer !== null) {
      window.clearTimeout(dropdownSelectionScrollTimer);
      dropdownSelectionScrollTimer = null;
    }

    if (dropdownSelectionTransitionHandler) {
      viewport.removeEventListener('transitionend', dropdownSelectionTransitionHandler);
      dropdownSelectionTransitionHandler = null;
    }
  };

  const cancelDropdownSelectionScroll = () => {
    clearDropdownSelectionScrollWait();

    if (dropdownSelectionScrollFrame !== null) {
      cancelAnimationFrame(dropdownSelectionScrollFrame);
      dropdownSelectionScrollFrame = null;
    }
  };

  const scrollFocusedRegionIntoView = (regionId) => {
    const mapRegion = mapRegions.get(regionId);

    if (!mapRegion || selectedRegionId !== regionId) {
      return;
    }

    const headerRect = document.querySelector('.header')?.getBoundingClientRect();
    const viewportRect = viewport.getBoundingClientRect();
    const visibleTop = (
      headerRect && headerRect.bottom > 0
        ? headerRect.bottom
        : 0
    ) + 20;
    const visibleBottom = document.documentElement.clientHeight - 20;
    const visibleHeight = visibleBottom - visibleTop;

    if (visibleHeight <= 0) {
      return;
    }

    const regionBounds = getRegionBaseBounds(mapRegion);
    const regionCenterY = (
      transform.y +
      (regionBounds.top + regionBounds.height / 2) * transform.scale
    );
    const regionScreenCenterY = viewportRect.top + regionCenterY;
    const comfortInset = clamp(visibleHeight * 0.28, 72, 180);
    const comfortableTop = visibleTop + comfortInset;
    const comfortableBottom = visibleBottom - comfortInset;

    if (
      regionScreenCenterY >= comfortableTop &&
      regionScreenCenterY <= comfortableBottom
    ) {
      return;
    }

    const targetScreenCenterY = visibleTop + visibleHeight * 0.46;
    const scrollTop = Math.max(
      0,
      window.scrollY + regionScreenCenterY - targetScreenCenterY
    );

    window.scrollTo({
      top: scrollTop,
      behavior: shouldReduceMotion() ? 'auto' : 'smooth',
    });
  };

  const scheduleDropdownSelectionScroll = (
    regionId,
    { waitForViewportResize = false } = {}
  ) => {
    cancelDropdownSelectionScroll();

    const scrollAfterFocus = () => {
      dropdownSelectionScrollFrame = null;

      if (selectedRegionId !== regionId) {
        return;
      }

      focusRegion(regionId, { animate: true });
      scrollFocusedRegionIntoView(regionId);
    };
    const queueScrollAfterFocus = () => {
      clearDropdownSelectionScrollWait();

      if (dropdownSelectionScrollFrame !== null) {
        cancelAnimationFrame(dropdownSelectionScrollFrame);
      }

      dropdownSelectionScrollFrame = requestAnimationFrame(scrollAfterFocus);
    };

    if (waitForViewportResize) {
      dropdownSelectionTransitionHandler = (event) => {
        if (event.target === viewport && event.propertyName === 'height') {
          queueScrollAfterFocus();
        }
      };
      viewport.addEventListener('transitionend', dropdownSelectionTransitionHandler);
      dropdownSelectionScrollTimer = window.setTimeout(queueScrollAfterFocus, 460);
      return;
    }

    dropdownSelectionScrollFrame = requestAnimationFrame(() => {
      dropdownSelectionScrollFrame = requestAnimationFrame(scrollAfterFocus);
    });
  };

  const scheduleSelectedRegionFocus = ({ animate = true } = {}) => {
    if (!selectedRegionId) {
      return;
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => focusRegion(selectedRegionId, { animate }));
    });
  };

  const clearMapModeIdleTimer = () => {
    if (mapModeIdleTimer !== null) {
      window.clearTimeout(mapModeIdleTimer);
      mapModeIdleTimer = null;
    }
  };

  const scheduleMapModeIdleTimer = () => {
    clearMapModeIdleTimer();

    if (!mapModeActive || !requiresExplicitMapMode()) {
      return;
    }

    mapModeIdleTimer = window.setTimeout(() => {
      mapModeIdleTimer = null;

      if (pointers.size > 0) {
        scheduleMapModeIdleTimer();
        return;
      }

      setMapMode(false, { focusSelected: false });
    }, mapModeIdleDelay);
  };

  const setMapMode = (active, { focusSelected = true } = {}) => {
    if (!requiresExplicitMapMode()) {
      active = false;
    }

    mapModeActive = active;
    geography.classList.toggle('is-map-mode', active);
    document.body.classList.toggle('geography-map-active', active);
    mapModeButton.setAttribute('aria-expanded', String(active));
    updateMapModePresentation();
    hideRegionTooltip();

    if (active) {
      scheduleMapModeIdleTimer();
    } else {
      clearMapModeIdleTimer();
    }

    scheduleMapModeButtonPositionUpdate();

    if (active && focusSelected && selectedRegionId) {
      scheduleSelectedRegionFocus({ animate: true });
    } else if (!active && selectedRegionId) {
      scheduleSelectedRegionFocus({ animate: true });
    } else {
      requestAnimationFrame(() => resetMap({ animate: !active }));
    }
  };

  const selectRegion = (regionId, control) => {
    const data = regionData.get(regionId);
    const mapRegion = mapRegions.get(regionId);
    const selectedFromDropdown = control?.classList.contains('region-item');
    const selectionExpandsViewport = (
      selectedFromDropdown &&
      mobileMedia.matches &&
      !geography.classList.contains('has-selection') &&
      !geography.classList.contains('is-map-mode') &&
      !shouldReduceMotion()
    );

    if (!data || !mapRegion) {
      return;
    }

    cancelDropdownSelectionScroll();

    selectedRegionId = regionId;
    lastSelectionControl = control || data.item;
    toggleLabel.textContent = data.name;
    panelTitle.textContent = data.name;
    panelSummary.textContent = `${data.count} ${getSchoolWord(data.count)}`;

    const schoolItems = data.schools.map((school) => {
      const item = document.createElement('li');
      const name = document.createElement('span');

      name.className = 'school-popup__school-name';
      name.textContent = school;
      item.append(name);
      return item;
    });

    if (schoolItems.length === 0) {
      const item = document.createElement('li');
      const name = document.createElement('span');

      item.className = 'school-popup__empty';
      name.className = 'school-popup__school-name';
      name.textContent = 'Список школ уточняется';
      item.append(name);
      schoolItems.push(item);
    }

    panelList.replaceChildren(...schoolItems);
    panelList.scrollTop = 0;
    panel.classList.add('active');
    panel.setAttribute('aria-hidden', 'false');
    geography.classList.add('has-selection');
    document.body.classList.add('geography-panel-active');
    hideRegionTooltip();
    mapRegionElements.forEach((region) => {
      const active = region.dataset.id === regionId;
      region.classList.toggle('active', active);
      region.setAttribute('aria-pressed', String(active));
    });
    regionItems.forEach((item) => {
      if (item.dataset.id === regionId) {
        item.setAttribute('aria-current', 'true');
      } else {
        item.removeAttribute('aria-current');
      }
    });
    setDropdownOpen(false);
    schedulePanelScrollbarUpdate();

    scheduleSelectedRegionFocus({ animate: true });

    if (selectedFromDropdown) {
      scheduleDropdownSelectionScroll(regionId, {
        waitForViewportResize: selectionExpandsViewport,
      });
    }
  };

  const clearSelection = ({ restoreFocus = false } = {}) => {
    const focusTarget = lastSelectionControl;

    cancelDropdownSelectionScroll();
    selectedRegionId = null;
    toggleLabel.textContent = 'Выберите регион';
    panel.classList.remove('active');
    panel.setAttribute('aria-hidden', 'true');
    geography.classList.remove('has-selection');
    document.body.classList.remove('geography-panel-active');
    schedulePanelScrollbarUpdate();
    hideRegionTooltip();
    mapRegionElements.forEach((region) => {
      region.classList.remove('active');
      region.setAttribute('aria-pressed', 'false');
    });
    regionItems.forEach((item) => item.removeAttribute('aria-current'));
    resetMap({ animate: true });

    if (restoreFocus && focusTarget?.isConnected) {
      focusTarget.focus({ preventScroll: true });
    }

    lastSelectionControl = null;
  };

  const markMapInteraction = () => {
    geography.classList.add('has-map-interaction');

    if (mapModeActive) {
      scheduleMapModeIdleTimer();
    }
  };

  const resolveMapRegionAtPoint = (clientX, clientY, fallbackRegion = null) => {
    const pointRegion = document
      .elementFromPoint(clientX, clientY)
      ?.closest('.schools__geography-map-region');

    return pointRegion || fallbackRegion;
  };

  toggleButton.addEventListener('click', () => {
    setDropdownOpen(!dropdown.classList.contains('open'));
  });

  toggleButton.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      setDropdownOpen(true, { focusSelected: true });
    }
  });

  regionList.addEventListener('click', (event) => {
    const item = event.target.closest('.region-item');

    if (item) {
      selectRegion(item.dataset.id, item);
    }
  });

  regionList.addEventListener('keydown', (event) => {
    const currentIndex = regionItems.indexOf(event.target.closest('.region-item'));

    if (currentIndex < 0) {
      return;
    }

    let nextIndex = currentIndex;

    if (event.key === 'ArrowDown') {
      nextIndex = (currentIndex + 1) % regionItems.length;
    } else if (event.key === 'ArrowUp') {
      nextIndex = (currentIndex - 1 + regionItems.length) % regionItems.length;
    } else if (event.key === 'Home') {
      nextIndex = 0;
    } else if (event.key === 'End') {
      nextIndex = regionItems.length - 1;
    } else if (event.key === 'Escape') {
      event.preventDefault();
      setDropdownOpen(false);
      toggleButton.focus();
      return;
    } else {
      return;
    }

    event.preventDefault();
    regionItems[nextIndex].focus();
  });

  mapRegionElements.forEach((region) => {
    region.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'touch') {
        showRegionTooltip(region, event);
      }
    });

    region.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'touch' && !pointerMoved) {
        showRegionTooltip(region, event);
      }
    });

    region.addEventListener('pointerleave', hideRegionTooltip);
    region.addEventListener('focus', () => showRegionTooltip(region));
    region.addEventListener('blur', hideRegionTooltip);
  });

  viewport.addEventListener('click', (event) => {
    if (
      suppressMapClick ||
      event.target.closest('button:not(.schools__geography-map-region)')
    ) {
      return;
    }

    const fallbackRegion = event.target.closest('.schools__geography-map-region');
    const region = event.detail === 0
      ? fallbackRegion
      : resolveMapRegionAtPoint(event.clientX, event.clientY, fallbackRegion);

    if (region) {
      markMapInteraction();
      selectRegion(region.dataset.id, region);
    }
  });

  panelCloseButton.addEventListener('click', () => {
    clearSelection({ restoreFocus: true });
  });

  panelList.addEventListener('scroll', schedulePanelScrollbarUpdate, { passive: true });
  panel.addEventListener('transitionend', (event) => {
    if (event.target === panel) {
      schedulePanelScrollbarUpdate();
    }
  });

  mapModeButton.addEventListener('click', () => {
    setMapMode(!mapModeActive, { focusSelected: true });
  });

  zoomInButton.addEventListener('click', () => {
    if (coarsePointerMedia.matches && !mapModeActive) {
      setMapMode(true, { focusSelected: false });
    }

    markMapInteraction();
    zoomFromCenter(1.38);
  });

  zoomOutButton.addEventListener('click', () => {
    if (coarsePointerMedia.matches && !mapModeActive) {
      setMapMode(true, { focusSelected: false });
    }

    markMapInteraction();
    zoomFromCenter(1 / 1.38);
  });

  resetButton.addEventListener('click', () => {
    markMapInteraction();
    resetMap({ animate: true });
  });

  viewport.addEventListener('wheel', (event) => {
    const intentionalZoom = event.ctrlKey || event.metaKey;

    if (!intentionalZoom || (requiresExplicitMapMode() && !mapModeActive)) {
      return;
    }

    event.preventDefault();
    markMapInteraction();
    hideRegionTooltip();

    const viewportRect = viewport.getBoundingClientRect();
    const normalizedDelta = event.deltaY * (
      event.deltaMode === WheelEvent.DOM_DELTA_LINE
        ? 16
        : event.deltaMode === WheelEvent.DOM_DELTA_PAGE
          ? viewport.clientHeight
          : 1
    );
    const factor = clamp(Math.exp(-normalizedDelta * 0.0055), 0.72, 1.38);

    zoomAtPoint(factor, {
      x: event.clientX - viewportRect.left,
      y: event.clientY - viewportRect.top,
    });
  }, { passive: false });

  const getPointerSnapshot = () => Array.from(pointers.values()).map((pointer) => ({ ...pointer }));

  const getPointDistance = (points) => Math.hypot(
    points[1].x - points[0].x,
    points[1].y - points[0].y
  );

  const getPointCenter = (points) => ({
    x: (points[0].x + points[1].x) / 2,
    y: (points[0].y + points[1].y) / 2,
  });

  viewport.addEventListener('pointerdown', (event) => {
    if (
      event.target.closest('button:not(.schools__geography-map-region)') ||
      (event.pointerType === 'mouse' && event.button !== 0) ||
      (requiresExplicitMapMode() && !mapModeActive)
    ) {
      return;
    }

    stopTransformAnimation();
    markMapInteraction();
    pointerMoved = false;
    pointers.set(event.pointerId, {
      pointerType: event.pointerType,
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
    });
    viewport.setPointerCapture(event.pointerId);
  });

  viewport.addEventListener('pointermove', (event) => {
    if (!pointers.has(event.pointerId)) {
      return;
    }

    const previousPoints = getPointerSnapshot();
    const currentPointer = pointers.get(event.pointerId);
    pointers.set(event.pointerId, {
      ...currentPointer,
      x: event.clientX,
      y: event.clientY,
    });
    const currentPoints = getPointerSnapshot();

    if (currentPoints.length >= 2 && previousPoints.length >= 2) {
      const previousDistance = Math.max(getPointDistance(previousPoints), 1);
      const currentDistance = Math.max(getPointDistance(currentPoints), 1);
      const previousCenter = getPointCenter(previousPoints);
      const currentCenter = getPointCenter(currentPoints);
      const viewportRect = viewport.getBoundingClientRect();
      const previousPoint = {
        x: previousCenter.x - viewportRect.left,
        y: previousCenter.y - viewportRect.top,
      };
      const currentPoint = {
        x: currentCenter.x - viewportRect.left,
        y: currentCenter.y - viewportRect.top,
      };
      const nextScale = clamp(
        transform.scale * currentDistance / previousDistance,
        1,
        getMaximumScale()
      );
      const sceneX = (previousPoint.x - transform.x) / transform.scale;
      const sceneY = (previousPoint.y - transform.y) / transform.scale;

      Object.assign(transform, constrainTransform({
        scale: nextScale,
        x: currentPoint.x - sceneX * nextScale,
        y: currentPoint.y - sceneY * nextScale,
      }));
    } else if (currentPoints.length === 1 && previousPoints.length === 1) {
      const pointer = currentPoints[0];
      const deltaX = currentPoints[0].x - previousPoints[0].x;
      const deltaY = currentPoints[0].y - previousPoints[0].y;
      const dragDistance = Math.hypot(
        pointer.x - pointer.startX,
        pointer.y - pointer.startY
      );
      const dragThreshold = pointer.pointerType === 'touch' ? 10 : 6;

      if (!pointerMoved && dragDistance >= dragThreshold) {
        pointerMoved = true;
      }

      if (pointerMoved) {
        Object.assign(transform, constrainTransform({
          scale: transform.scale,
          x: transform.x + deltaX,
          y: transform.y + deltaY,
        }));
      }
    }

    if (pointerMoved || currentPoints.length >= 2) {
      pointerMoved = true;
      viewport.classList.add('is-panning');
      markMapInteraction();
      hideRegionTooltip();
      event.preventDefault();
      scheduleTransformRender();
    }
  }, { passive: false });

  const releasePointer = (event) => {
    if (!pointers.has(event.pointerId)) {
      return;
    }

    pointers.delete(event.pointerId);

    if (viewport.hasPointerCapture(event.pointerId)) {
      viewport.releasePointerCapture(event.pointerId);
    }

    if (pointers.size === 0) {
      viewport.classList.remove('is-panning');

      if (pointerMoved) {
        suppressMapClick = true;
        window.setTimeout(() => {
          suppressMapClick = false;
          pointerMoved = false;
        }, 0);
      }
    }
  };

  viewport.addEventListener('pointerup', releasePointer);
  viewport.addEventListener('pointercancel', releasePointer);

  viewport.addEventListener('keydown', (event) => {
    if (event.target.closest('button')) {
      return;
    }

    const panStep = event.shiftKey ? 96 : 48;
    let handled = true;

    if (event.key === '+' || event.key === '=') {
      zoomFromCenter(1.38);
    } else if (event.key === '-' || event.key === '_') {
      zoomFromCenter(1 / 1.38);
    } else if (event.key === '0' || event.key === 'Home') {
      resetMap({ animate: true });
    } else if (event.key === 'ArrowLeft') {
      setTransform({ ...transform, x: transform.x + panStep }, { animate: true });
    } else if (event.key === 'ArrowRight') {
      setTransform({ ...transform, x: transform.x - panStep }, { animate: true });
    } else if (event.key === 'ArrowUp') {
      setTransform({ ...transform, y: transform.y + panStep }, { animate: true });
    } else if (event.key === 'ArrowDown') {
      setTransform({ ...transform, y: transform.y - panStep }, { animate: true });
    } else if (event.key === 'Escape' && mapModeActive) {
      setMapMode(false, { focusSelected: false });
    } else {
      handled = false;
    }

    if (handled) {
      event.preventDefault();
      markMapInteraction();
    }
  });

  document.addEventListener('click', (event) => {
    if (!dropdown.contains(event.target)) {
      setDropdownOpen(false);
    }
  });

  const updateLayout = () => {
    resizeFrame = null;
    stopTransformAnimation();

    if (!requiresExplicitMapMode() && mapModeActive) {
      clearMapModeIdleTimer();
      mapModeActive = false;
      geography.classList.remove('is-map-mode');
      geography.classList.remove('has-floating-map-mode-toggle');
      document.body.classList.remove('geography-map-active');
      mapModeButton.setAttribute('aria-expanded', 'false');
    }

    updateMapModePresentation();
    scheduleMapModeButtonPositionUpdate();
    schedulePanelScrollbarUpdate();

    if (selectedRegionId) {
      focusRegion(selectedRegionId, { animate: false });
    } else {
      resetMap({ animate: false });
    }
  };

  const scheduleLayoutUpdate = () => {
    if (resizeFrame !== null) {
      cancelAnimationFrame(resizeFrame);
    }

    resizeFrame = requestAnimationFrame(updateLayout);
  };

  window.addEventListener('resize', scheduleLayoutUpdate, { passive: true });
  window.addEventListener('scroll', scheduleMapModeButtonPositionUpdate, { passive: true });
  mobileMedia.addEventListener?.('change', scheduleLayoutUpdate);
  desktopMedia.addEventListener?.('change', scheduleLayoutUpdate);
  coarsePointerMedia.addEventListener?.('change', scheduleLayoutUpdate);

  if (typeof ResizeObserver === 'function') {
    const resizeObserver = new ResizeObserver(scheduleLayoutUpdate);
    const panelResizeObserver = new ResizeObserver(schedulePanelScrollbarUpdate);

    resizeObserver.observe(viewport);
    panelResizeObserver.observe(panel);
    panelResizeObserver.observe(panelList);
  }

  if (typeof IntersectionObserver === 'function') {
    const visibilityObserver = new IntersectionObserver(([entry]) => {
      document.body.classList.toggle('geography-map-visible', entry.isIntersecting);
      scheduleMapModeButtonPositionUpdate();
    }, { threshold: 0.08 });

    visibilityObserver.observe(geography);
  }

  setDropdownOpen(false);
  updateMapModePresentation();
  requestAnimationFrame(() => {
    resetMap({ animate: false });
    updatePanelScrollbar();
  });
})();
